import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import { UNIT_STATUSES, unitStatusBadge, unitStatusLabel } from "../lib/units";

const COLUMNS = ["Unit", "Building", "Floor", "Status", "Current tenant"];

export function UnitsPage() {
  const [building, setBuilding] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const buildingsQ = useQuery({
    queryKey: ["ref", "buildings"],
    queryFn: () => api.reference.buildings(),
  });
  const listQ = useQuery({
    queryKey: ["units", { building, status }],
    queryFn: () => api.units.list({ building, status }),
  });

  const all = listQ.data?.units ?? [];
  const term = q.trim().toLowerCase();
  const rows = term
    ? all.filter((u) =>
        [u.unitNumber, u.buildingName, u.currentTenantName ?? ""].some((v) =>
          v.toLowerCase().includes(term),
        ),
      )
    : all;

  return (
    <Layout title="Units">
      <div className="filters">
        <label className="field">
          <span>Search</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Unit or tenant..." />
        </label>
        <label className="field">
          <span>Building</span>
          <select value={building} onChange={(e) => setBuilding(e.target.value)}>
            <option value="all">All buildings</option>
            {(buildingsQ.data?.buildings ?? []).map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All</option>
            {UNIT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {unitStatusLabel(s)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <section className="card">
        {listQ.isLoading ? (
          <p className="sub">Loading...</p>
        ) : listQ.isError ? (
          <p className="sub">Could not load units. Please try again.</p>
        ) : rows.length === 0 ? (
          <p className="sub">No units match these filters.</p>
        ) : (
          <div className="table-wrap stack-md">
            <table>
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id}>
                    <td data-label="Unit">
                      <Link to={`/units/${u.id}`} className="wo-title-link">
                        Unit {u.unitNumber}
                      </Link>
                    </td>
                    <td data-label="Building">{u.buildingName}</td>
                    <td data-label="Floor">{u.floor ?? "-"}</td>
                    <td data-label="Status">
                      <span className={`badge badge-${unitStatusBadge(u.status)}`}>
                        {unitStatusLabel(u.status)}
                      </span>
                    </td>
                    <td data-label="Current tenant">{u.currentTenantName ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
}
